import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import theme from "../../config/theme";
import Layout from "../../layout/layout";
import Input from "../../common/input";
import Button from "../../common/button";
import Toggle from "../../common/toggle";
import FileUpload from "../../common/fileUpload";
import MobileSimulator from "../../common/mobileSimulator";
import Dropdown from "../../common/dropdown";
import DateInput from "../../common/dateInput";
import SnackBar from "../../common/snackBar";
import Breadcrumb from "../../common/breadcrumb";
import TemplateDialog, {
  Template,
  UploadedImageSize,
  getTemplateTypeFromImageSize,
} from "./template.dialog";
import { FaChevronLeft } from "react-icons/fa";
import { IoCheckmarkCircle } from "react-icons/io5";
import { fileApi, richMenuApi, segmentsApi } from "../../utils/apiClient";
import { PCSegmentDataDto } from "../../dtos/pcSegment.dto";
import { getErrorMessage } from "../../utils/errorMessages";

interface ActionItem {
  id: string;
  coordinates: string;
  actionType: string;
  inputValue: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

const maxMenuBarTextLength = 14;

const ModifyRichMenuPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editMode = !!id;
  const editId = id || null;

  // Form states
  const [menuName, setMenuName] = useState("");
  const [menuBarText, setMenuBarText] = useState("Menu");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Helper function to normalize date string (YYYY-MM-DD) to Date object (remove time & timezone issues)
  const normalizeDateString = (dateString: string): Date | null => {
    if (!dateString) return null;
    const parts = dateString.split("-");
    if (parts.length !== 3) return null;
    const [yearStr, monthStr, dayStr] = parts;
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };
  const [setAsDefaultMenu, setSetAsDefaultMenu] = useState(true);
  const [showOnChatOpen, setShowOnChatOpen] = useState(true);
  const [targetSegment, setTargetSegment] = useState<"all" | "targeting">(
    "all"
  );
  const [selectedSegment, setSelectedSegment] = useState<string | null>(null);
  const [segments, setSegments] = useState<PCSegmentDataDto[]>([]);
  const [isLoadingSegments, setIsLoadingSegments] = useState(false);
  const [segmentsError, setSegmentsError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploadedImageSize, setUploadedImageSize] =
    useState<UploadedImageSize | null>(null);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [showUploadSnackbar, setShowUploadSnackbar] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    "Richmenu Created Successfully"
  );
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingData, setIsLoadingData] = useState(editMode);
  const [currentActiveStatus, setCurrentActiveStatus] = useState<
    boolean | null
  >(null); // Store current active status when editing

  // Default template: 1-2equal (small)
  const defaultTemplate: Template = {
    id: "small-1-2equal",
    name: "1 Wide + 2 Equal",
    type: "small",
    layout: "1x2equal",
  };

  // Generate cell count from template layout
  const getCellCount = (layout: string): number => {
    const layoutMap: Record<string, number> = {
      "2x3": 6,
      "2x2": 4,
      "1-2equal": 3,
      "1-2unequal": 3,
      "2rows": 2,
      "2cols": 2,
      "1rect": 1,
      "1x3": 3,
      "1x2bigRight": 2,
      "1x2bigLeft": 2,
      "1x2equal": 2,
      "1rectSmall": 1,
    };
    return layoutMap[layout] || 0;
  };

  // Generate cell labels (A, B, C, etc.)
  const generateCellLabels = (count: number): string[] => {
    const labels: string[] = [];
    for (let i = 0; i < count; i++) {
      labels.push(String.fromCharCode(65 + i)); // A, B, C, ...
    }
    return labels;
  };

  // Calculate coordinates based on template and cell position
  const calculateCellCoordinates = (
    template: Template,
    cellIndex: number,
    totalCells: number
  ): { x: number; y: number; width: number; height: number } => {
    const isLarge = template.type === "large";
    const imageWidth = isLarge ? 2500 : 2500;
    const imageHeight = isLarge ? 1686 : 843;

    // Default coordinates - will be calculated based on layout
    let x = 0,
      y = 0,
      width = 0,
      height = 0;

    switch (template.layout) {
      case "1x2equal":
        width = imageWidth / 2;
        height = imageHeight;
        x = cellIndex * width;
        y = 0;
        break;
      case "2x2":
        width = imageWidth / 2;
        height = imageHeight / 2;
        x = (cellIndex % 2) * width;
        y = Math.floor(cellIndex / 2) * height;
        break;
      case "2x3":
        width = imageWidth / 3;
        height = imageHeight / 2;
        x = (cellIndex % 3) * width;
        y = Math.floor(cellIndex / 3) * height;
        break;
      case "1-2equal":
        if (cellIndex === 0) {
          width = imageWidth;
          height = imageHeight / 3;
          x = 0;
          y = 0;
        } else {
          width = imageWidth / 2;
          height = (imageHeight * 2) / 3;
          x = (cellIndex - 1) * width;
          y = imageHeight / 3;
        }
        break;
      case "1-2unequal":
        if (cellIndex === 0) {
          width = imageWidth;
          height = imageHeight / 4;
          x = 0;
          y = 0;
        } else if (cellIndex === 1) {
          width = (imageWidth * 2) / 3;
          height = (imageHeight * 3) / 4;
          x = 0;
          y = imageHeight / 4;
        } else {
          width = imageWidth / 3;
          height = (imageHeight * 3) / 4;
          x = (imageWidth * 2) / 3;
          y = imageHeight / 4;
        }
        break;
      case "1x3":
        width = imageWidth / 3;
        height = imageHeight;
        x = cellIndex * width;
        y = 0;
        break;
      case "1x2bigRight":
        if (cellIndex === 0) {
          width = imageWidth / 3;
          height = imageHeight;
          x = 0;
          y = 0;
        } else {
          width = (imageWidth * 2) / 3;
          height = imageHeight;
          x = imageWidth / 3;
          y = 0;
        }
        break;
      case "1x2bigLeft":
        if (cellIndex === 0) {
          width = (imageWidth * 2) / 3;
          height = imageHeight;
          x = 0;
          y = 0;
        } else {
          width = imageWidth / 3;
          height = imageHeight;
          x = (imageWidth * 2) / 3;
          y = 0;
        }
        break;
      case "2rows":
        width = imageWidth;
        height = imageHeight / 2;
        x = 0;
        y = cellIndex * height;
        break;
      case "2cols":
        width = imageWidth / 2;
        height = imageHeight;
        x = cellIndex * width;
        y = 0;
        break;
      case "1rect":
      case "1rectSmall":
        width = imageWidth;
        height = imageHeight;
        x = 0;
        y = 0;
        break;
      default:
        // Default calculation
        width = imageWidth / totalCells;
        height = imageHeight;
        x = cellIndex * width;
        y = 0;
    }

    return {
      x: Math.round(x),
      y: Math.round(y),
      width: Math.round(width),
      height: Math.round(height),
    };
  };

  // Create actions based on template
  const createActionsFromTemplate = React.useCallback(
    (template: Template): ActionItem[] => {
      const cellCount = getCellCount(template.layout);
      const labels = generateCellLabels(cellCount);

      return labels.map((label, index) => {
        const coords = calculateCellCoordinates(template, index, cellCount);
        return {
          id: `${template.id}-${label}-${Date.now()}-${index}`,
          coordinates: `Cell ${label}`,
          actionType: "Message",
          inputValue: "",
          x: coords.x,
          y: coords.y,
          width: coords.width,
          height: coords.height,
        };
      });
    },
    []
  );

  const [selectedTemplate, setSelectedTemplate] = useState<
    Template | undefined
  >(defaultTemplate);

  const [actions, setActions] = useState<ActionItem[]>([]);

  // Initialize default template and actions on mount
  useEffect(() => {
    if (selectedTemplate) {
      const initialActions = createActionsFromTemplate(selectedTemplate);
      setActions(initialActions);
    }
  }, []);

  // Load segments list from backend on mount
  useEffect(() => {
    const fetchSegments = async () => {
      setIsLoadingSegments(true);
      setSegmentsError(null);
      try {
        const response = await segmentsApi.searchSegments(undefined, 0, 100);
        const segmentsList = response.data || [];
        setSegments(segmentsList);

        // If selectedSegment is a segment name (not an ID), find and update to ID
        if (selectedSegment && targetSegment === "targeting") {
          // Check if selectedSegment is a name (not a numeric ID)
          const isName = !/^\d+$/.test(selectedSegment);
          if (isName) {
            const segment = segmentsList.find(
              (s) => s.segmentName === selectedSegment
            );
            if (segment) {
              setSelectedSegment(String(segment.id));
            }
          }
        }
      } catch (error) {
        console.error("Failed to load segments:", error);
        setSegmentsError("Failed to load segments");
      } finally {
        setIsLoadingSegments(false);
      }
    };

    fetchSegments();
  }, [selectedSegment, targetSegment]);

  // Fetch rich menu data when editing
  useEffect(() => {
    const fetchRichMenuData = async () => {
      if (!editMode || !editId) return;

      setIsLoadingData(true);
      try {
        const data = await richMenuApi.getRichMenuById(editId);

        // Prefill form fields with fetched data
        setMenuName(data.name || "");
        setMenuBarText(data.chatBarText || "");
        setStartDate(data.startDate || "");
        setEndDate(data.endDate || "");
        setSetAsDefaultMenu(data.isDefault || false);
        setShowOnChatOpen(data.selected || false);
        setImageUrl(data.imageUrl || null);
        // Store current active status for update payload
        setCurrentActiveStatus(data.active ?? false);

        // Handle segment - segmentName can be "all", a segment ID (numeric string), or segment name
        // If it's "targeting", it's old data format - we'll need to wait for segments to load
        if (data.segmentName && data.segmentName.toLowerCase() !== "all") {
          setTargetSegment("targeting");

          // Check if segmentName is a numeric ID (new format)
          const isNumericId = /^\d+$/.test(String(data.segmentName));

          if (isNumericId) {
            // segmentName is already the segment ID
            setSelectedSegment(String(data.segmentName));
          } else if (data.segmentName.toLowerCase() === "targeting") {
            // Old format - "targeting" string, need to find actual segment
            // This might not work if we don't know which segment was selected
            // Store temporarily and let the segments loading effect handle it
            setSelectedSegment(null);
          } else {
            // segmentName is a segment name - find segment by name to get its ID
            const segment = segments.find(
              (s) => s.segmentName === data.segmentName
            );
            if (segment) {
              setSelectedSegment(String(segment.id));
            } else {
              // Store segment name temporarily - will be converted to ID when segments load
              setSelectedSegment(data.segmentName);
            }
          }
        } else {
          setTargetSegment("all");
          setSelectedSegment(null);
        }

        // Handle template and actions from areas
        if (data.areas && data.areas.length > 0) {
          // Map areas to actions
          const mappedActions: ActionItem[] = data.areas.map((area, index) => {
            const label = String.fromCharCode(65 + index); // A, B, C, etc.
            return {
              id: `action-${index}-${Date.now()}`,
              coordinates: `Cell ${label}`,
              actionType:
                area.action.type === "message"
                  ? "Message"
                  : area.action.type === "uri"
                  ? "URL"
                  : area.action.type === "postback"
                  ? "Postback"
                  : "Message",
              inputValue:
                area.action.text || area.action.uri || area.action.data || "",
              x: area.bounds.x,
              y: area.bounds.y,
              width: area.bounds.width,
              height: area.bounds.height,
            };
          });

          // Determine template - prioritize selectedTemplate from API
          let template: Template;

          if (data.selectedTemplate) {
            // Use the template from API response
            template = {
              id: data.selectedTemplate.id,
              name: data.selectedTemplate.name,
              type: data.selectedTemplate.type as "large" | "small",
              layout: data.selectedTemplate.layout,
            };
          } else {
            // Fallback: Determine template from layout based on cell count
            const cellCount = mappedActions.length;
            let templateLayout = "1x2equal"; // default
            if (cellCount === 1) templateLayout = "1rect";
            else if (cellCount === 2) templateLayout = "1x2equal";
            else if (cellCount === 3) templateLayout = "1-2equal";
            else if (cellCount === 4) templateLayout = "2x2";
            else if (cellCount === 6) templateLayout = "2x3";

            template = {
              id: `template-${templateLayout}`,
              name: `Template ${cellCount} cells`,
              type: (data.height || 843) > 1000 ? "large" : "small",
              layout: templateLayout,
            };
          }

          setSelectedTemplate(template);
          setActions(mappedActions);
        }
      } catch (error) {
        console.error("Failed to load rich menu data:", error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchRichMenuData();
  }, [editMode, editId]);

  const actionTypeOptions = [
    { value: "Message", label: "Message" },
    { value: "URL", label: "URL" },
    { value: "Postback", label: "Postback" },
  ];

  // Validate rich menu image dimensions and aspect ratio
  const validateRichMenuImage = (
    file: File
  ): Promise<{
    valid: boolean;
    error?: string;
    width?: number;
    height?: number;
  }> => {
    return new Promise((resolve) => {
      // Check file type
      const validTypes = ["image/jpeg", "image/png"];
      if (!validTypes.includes(file.type)) {
        resolve({ valid: false, error: "Image format must be JPEG or PNG" });
        return;
      }

      // Check file size (max 1 MB)
      const maxSizeBytes = 1 * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        resolve({
          valid: false,
          error: "Image file size must be 1 MB or less",
        });
        return;
      }

      // Create image element to check dimensions
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        const width = img.width;
        const height = img.height;
        const aspectRatio = width / height;

        // Validate width: 800 to 2500 pixels
        if (width < 800 || width > 2500) {
          resolve({
            valid: false,
            error: `Image width must be between 800 and 2500 pixels (current: ${width}px)`,
          });
          return;
        }

        // Validate height: 250 pixels or more
        if (height < 250) {
          resolve({
            valid: false,
            error: `Image height must be 250 pixels or more (current: ${height}px)`,
          });
          return;
        }

        // Validate aspect ratio: 1.45 or more
        if (aspectRatio < 1.45) {
          resolve({
            valid: false,
            error: `Image aspect ratio (width/height) must be 1.45 or more (current: ${aspectRatio.toFixed(
              2
            )})`,
          });
          return;
        }

        // If template is already selected, validate image matches template size
        if (selectedTemplate) {
          const imageTemplateType = getTemplateTypeFromImageSize(width, height);
          if (
            imageTemplateType &&
            imageTemplateType !== selectedTemplate.type
          ) {
            const expectedHeight =
              selectedTemplate.type === "large" ? 1686 : 843;
            resolve({
              valid: false,
              error: `รูปภาพไม่ตรงกับ template ที่เลือก (${
                selectedTemplate.type === "large" ? "Large" : "Small"
              }) กรุณาใช้รูปขนาด 2500 x ${expectedHeight} px`,
            });
            return;
          }
        }

        resolve({ valid: true, width, height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ valid: false, error: "Failed to load image for validation" });
      };

      img.src = objectUrl;
    });
  };

  const handleFileSelect = async (file: File) => {
    // Validate image before uploading
    const validationResult = await validateRichMenuImage(file);
    if (!validationResult.valid) {
      setErrorMessage(validationResult.error || "Invalid image");
      setShowErrorSnackbar(true);
      return;
    }

    // Store image dimensions for template filtering
    if (validationResult.width && validationResult.height) {
      setUploadedImageSize({
        width: validationResult.width,
        height: validationResult.height,
      });
    }

    setSelectedFile(file);

    // Optional: you can show a local preview using FileUpload's internal preview

    try {
      // Upload file immediately when it is changed
      const uploadResult = await fileApi.uploadRichMenuImage(file, "richmenus");
      console.log("Rich menu image uploaded:", uploadResult);

      // Try to extract image URL from response (adjust keys based on backend)
      const uploadedUrl =
        uploadResult?.url ||
        uploadResult?.imageUrl ||
        uploadResult?.path ||
        uploadResult?.filePath ||
        null;

      if (uploadedUrl) {
        setImageUrl(uploadedUrl);
      }

      // Show success snackbar
      setShowUploadSnackbar(true);
    } catch (error) {
      console.error("Failed to upload rich menu image:", error);
      setErrorMessage(getErrorMessage(error));
      setShowErrorSnackbar(true);
    }
  };

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const handleActionTypeChange = (actionId: string, newType: string) => {
    setActions((prev) =>
      prev.map((action) =>
        action.id === actionId ? { ...action, actionType: newType } : action
      )
    );
  };

  const handleActionInputChange = (actionId: string, newValue: string) => {
    setActions((prev) =>
      prev.map((action) =>
        action.id === actionId ? { ...action, inputValue: newValue } : action
      )
    );
  };

  const getActionPlaceholder = (actionType: string) => {
    switch (actionType) {
      case "Message":
        return "เช่น สวัสดีค่ะ ต้องการความช่วยเหลือ";
      case "URL":
        return "เช่น https://www.example.com";
      case "Postback":
        return "เช่น action=register&type=member";
      default:
        return "Enter value...";
    }
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);

    // If the new start date is after the current end date, clear the end date to avoid invalid range
    if (endDate) {
      const newStart = normalizeDateString(value);
      const currentEnd = normalizeDateString(endDate);
      if (newStart && currentEnd && newStart > currentEnd) {
        setEndDate("");
      }
    }
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);

    // If the new end date is before the current start date, clear the start date to avoid invalid range
    if (startDate) {
      const newEnd = normalizeDateString(value);
      const currentStart = normalizeDateString(startDate);
      if (newEnd && currentStart && newEnd < currentStart) {
        setStartDate("");
      }
    }
  };

  const handleSubmit = async () => {
    // Build payload to match backend rich menu API
    // When using targeting, segmentName should contain the segment ID, not the segment name or "targeting"
    // Remove targetSegment from payload - only use segmentName to indicate targeting
    console.log("selectedSegment", selectedSegment);
    console.log("targetSegment", targetSegment);
    const segmentVal =
      targetSegment === "all" ? "all" : selectedSegment || "all";
    const segmentName = segmentVal;

    // Active status: always true when creating, use current status when updating (or false if not available)
    const active = editMode
      ? currentActiveStatus !== null
        ? currentActiveStatus
        : false
      : true;

    // Transform actions from UI format to API format
    const transformedActions = actions.map((action) => {
      const baseAction = {
        bounds: {
          x: action.x || 0,
          y: action.y || 0,
          width: action.width || 0,
          height: action.height || 0,
        },
      };

      // Convert UI actionType to API format
      if (action.actionType === "URL") {
        return {
          ...baseAction,
          action: {
            type: "uri",
            uri: action.inputValue || "",
          },
        };
      } else if (action.actionType === "Postback") {
        return {
          ...baseAction,
          action: {
            type: "postback",
            data: action.inputValue || "",
          },
        };
      } else {
        // Default to Message
        return {
          ...baseAction,
          action: {
            type: "message",
            text: action.inputValue || "",
          },
        };
      }
    });

    const payload = {
      mode: editMode ? "edit" : "create",
      editId: editMode && editId ? Number(editId) : null,
      menuName,
      menuBarText,
      startDate,
      endDate,
      setAsDefaultMenu,
      showOnChatOpen,
      selectedSegment:
        targetSegment === "all"
          ? null
          : selectedSegment
          ? String(selectedSegment)
          : null,
      segmentName, // Map segment ID when targeting, "all" when all friends - NEVER send "targeting"
      selectedTemplate,
      actions: transformedActions,
      imageUrl,
      hasImage: !!selectedFile,
      active, // true when creating, current status when updating
    };

    console.log("Rich Menu submit payload:", payload);

    setIsSubmitting(true);
    try {
      if (editMode && editId) {
        // Use PUT for edit
        await richMenuApi.updateRichMenu(editId, payload);
        setSuccessMessage("Richmenu Updated Successfully");
      } else {
        // Use POST for create
        await richMenuApi.createRichMenu(payload);
        setSuccessMessage("Richmenu Created Successfully");
      }
      setShowSuccessSnackbar(true);
      // Redirect to rich menu list after a short delay to show success message
      setTimeout(() => {
        navigate("/rich-menu");
      }, 1500);
    } catch (error) {
      console.error("Failed to create/update rich menu:", error);
      // Show error snackbar with user-friendly message
      setErrorMessage(getErrorMessage(error));
      setShowErrorSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasValidActions =
    actions.length > 0 && actions.every((action) => action.inputValue.trim());

  // Target segment is valid when:
  // - "All Friends" is selected, or
  // - "Targeting" is selected AND a segment is chosen
  const hasValidTargetSegment =
    targetSegment === "all" ||
    (targetSegment === "targeting" && !!selectedSegment);

  const isFormValid = !!(
    menuName.trim() &&
    menuBarText.trim() &&
    startDate &&
    endDate &&
    imageUrl &&
    hasValidActions &&
    hasValidTargetSegment
  );

  // Show loading state while fetching data
  if (isLoadingData) {
    return (
      <Layout currentPath="/rich-menu">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-sm" style={{ color: theme.color.gray300 }}>
            Loading...
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPath="/rich-menu">
      {/* Stacked SnackBar container */}
      <div className="fixed top-20 right-4 z-50 flex flex-col gap-2">
        {showUploadSnackbar && (
          <SnackBar
            message="File Uploaded Successfully"
            type="success"
            position="inline"
            show={showUploadSnackbar}
            onClose={() => setShowUploadSnackbar(false)}
          />
        )}
        {showSuccessSnackbar && (
          <SnackBar
            message={successMessage}
            type="success"
            position="inline"
            show={showSuccessSnackbar}
            onClose={() => setShowSuccessSnackbar(false)}
          />
        )}
        {showErrorSnackbar && (
          <SnackBar
            message={errorMessage}
            type="error"
            position="inline"
            show={showErrorSnackbar}
            onClose={() => setShowErrorSnackbar(false)}
          />
        )}
      </div>
      <div className="min-h-screen bg-gray-50 pb-20">
        {/* Header */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate("/rich-menu")}
            className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shadow-sm border transition-colors hover:bg-gray-50"
            style={{
              borderColor: theme.color.light300,
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            {React.createElement(FaChevronLeft as any, {
              className: "w-3 h-3",
              style: { color: theme.color.gray500 },
            })}
          </button>
          <div className="ml-3">
            <Breadcrumb
              items={[
                {
                  label: "LINE Rich Menu",
                  onClick: () => navigate("/rich-menu"),
                },
                { label: editMode ? "Edit Rich Menu" : "Create Rich Menu" },
              ]}
            />
          </div>
        </div>

        <div className="flex justify-between gap-8">
          {/* Left Column - Form */}
          <div>
            <MobileSimulator
              template={selectedTemplate}
              imageUrl={imageUrl}
              showOnChatOpen={showOnChatOpen}
              menuBarText={menuBarText}
            />
          </div>

          {/* Right Column - Preview */}
          <div className="w-full space-y-8">
            {/* Menu Settings */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h2
                className="text-lg  mb-4"
                style={{ color: theme.color.gray500 }}
              >
                Menu Settings
              </h2>

              <div className="space-y-4">
                {/* Input Fields - 2 Columns */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div>
                    <Input
                      label="Menu Name"
                      placeholder="e.g., Promotion Menu"
                      value={menuName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setMenuName(e.target.value)
                      }
                      required
                    />
                  </div>

                  {/* Right Column */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label
                        className="block text-sm"
                        style={{ color: theme.color.gray400 }}
                      >
                        Menu Bar Text
                        <span
                          className="ml-1"
                          style={{ color: theme.brandColor.red }}
                        >
                          *
                        </span>
                      </label>
                      <span
                        className="text-sm"
                        style={{ color: theme.color.gray300 }}
                      >
                        {menuBarText.length}/{maxMenuBarTextLength}
                      </span>
                    </div>
                    <Input
                      placeholder="e.g., Special Promotion"
                      value={menuBarText}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setMenuBarText(e.target.value)
                      }
                      required
                      maxLength={maxMenuBarTextLength}
                    />
                  </div>
                </div>

                {/* Date Fields - 2 Columns */}
                <div className="grid grid-cols-2 gap-4">
                  <DateInput
                    label="Start Date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    placeholder="Select start date"
                    required
                    maxDate={normalizeDateString(endDate)}
                  />

                  <DateInput
                    label="End Date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    placeholder="Select end date"
                    required
                    minDate={normalizeDateString(startDate)}
                  />
                </div>

                {/* Toggles - 2 Columns */}
                <div className="grid grid-cols-2 gap-4">
                  <div
                    className="w-full px-4 py-3 rounded-lg border mt-4"
                    style={{
                      borderColor: theme.color.light500,
                      backgroundColor: theme.color.light100,
                    }}
                  >
                    <Toggle
                      label="Set as main rich menu"
                      checked={setAsDefaultMenu}
                      onChange={setSetAsDefaultMenu}
                      colorChecked={theme.color.greenLight}
                      className="w-full justify-between"
                    />
                  </div>

                  <div
                    className="w-full px-4 py-3 rounded-lg border mt-4"
                    style={{
                      borderColor: theme.color.light500,
                      backgroundColor: theme.color.light100,
                    }}
                  >
                    <Toggle
                      label="Show menu on chat open"
                      checked={showOnChatOpen}
                      onChange={setShowOnChatOpen}
                      colorChecked={theme.color.greenLight}
                      className="w-full justify-between"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Upload Rich Menu Image */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h2
                className="text-lg mb-4 flex items-center gap-1"
                style={{ color: theme.color.gray500 }}
              >
                Upload Rich Menu Image
                <span style={{ color: theme.brandColor.red }}>*</span>
              </h2>
              <FileUpload
                onFileSelect={handleFileSelect}
                selectedFile={selectedFile}
                onFileRemove={() => {
                  setSelectedFile(null);
                  setImageUrl(null);
                  setUploadedImageSize(null);
                }}
                accept=".jpg,.jpeg,.png"
                maxSize={1}
                onValidationError={(error) => {
                  setErrorMessage(error);
                  setShowErrorSnackbar(true);
                }}
                hideRequirements={true}
              />
              {/* Dynamic Image Requirements based on selected template */}
              <div
                className="mt-4 text-sm space-y-1"
                style={{ color: theme.color.light }}
              >
                <div className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Max file size: 1 MB</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>File formats: JPG, JPEG, PNG</span>
                </div>
                <div className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>
                    Image size: width 2500px × height{" "}
                    {selectedTemplate?.type === "large" ? (
                      <strong style={{ color: theme.color.gray500 }}>
                        1686px (Large)
                      </strong>
                    ) : selectedTemplate?.type === "small" ? (
                      <strong style={{ color: theme.color.gray500 }}>
                        843px (Small)
                      </strong>
                    ) : (
                      "843px (Small) / 1686px (Large)"
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Action */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2
                  className="text-lg font-semibold flex items-center gap-1"
                  style={{ color: theme.color.gray500 }}
                >
                  Action
                  <span style={{ color: theme.brandColor.red }}>*</span>
                </h2>
                <Button
                  variant="primary"
                  onClick={() => setIsTemplateDialogOpen(true)}
                >
                  Select Template
                </Button>
              </div>

              <div className="space-y-4">
                {actions.length > 0 ? (
                  actions.map((action) => {
                    const cellLabel = action.coordinates.replace("Cell ", "");
                    return (
                      <div
                        key={action.id}
                        className="rounded-lg p-4"
                        style={{
                          backgroundColor: theme.color.light200,
                          borderColor: theme.color.light300,
                        }}
                      >
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <h3
                                className="font-semibold"
                                style={{ color: theme.color.gray500 }}
                              >
                                Action {cellLabel}
                              </h3>
                              {action.x !== undefined &&
                                action.y !== undefined &&
                                action.width !== undefined &&
                                action.height !== undefined && (
                                  <span
                                    className="text-sm ml-2"
                                    style={{ color: theme.color.gray300 }}
                                  >
                                    (x: {action.x} | y: {action.y} | width:{" "}
                                    {action.width} | height: {action.height})
                                  </span>
                                )}
                            </div>
                            <div className="flex-1 max-w-xs ml-4">
                              <Dropdown
                                options={actionTypeOptions}
                                value={action.actionType}
                                onChange={(value: string | number) =>
                                  handleActionTypeChange(
                                    action.id,
                                    value as string
                                  )
                                }
                                placeholder="Select action type"
                                label="Action Type"
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <Input
                            placeholder={getActionPlaceholder(
                              action.actionType
                            )}
                            value={action.inputValue}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) =>
                              handleActionInputChange(action.id, e.target.value)
                            }
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    className="text-center py-8"
                    style={{ color: theme.color.gray300 }}
                  >
                    <p>
                      No actions configured. Please select a template first.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Target Segment */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h2
                className="text-lg font-semibold mb-4"
                style={{ color: theme.color.gray500 }}
              >
                Target Segment
              </h2>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setTargetSegment("all");
                    setSelectedSegment(null);
                  }}
                  className="flex-1 flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all"
                  style={{
                    borderColor:
                      targetSegment === "all"
                        ? theme.brandColor.red
                        : theme.color.light500,
                    backgroundColor:
                      targetSegment === "all" ? "transparent" : "transparent",
                  }}
                >
                  <span
                    className="text-sm font-medium"
                    style={{
                      color:
                        targetSegment === "all"
                          ? theme.brandColor.red
                          : theme.color.gray500,
                    }}
                  >
                    All Friends
                  </span>
                  {targetSegment === "all" ? (
                    React.createElement(IoCheckmarkCircle as any, {
                      className: "w-5 h-5",
                      style: { color: theme.brandColor.red },
                    })
                  ) : (
                    <div
                      className="w-5 h-5 rounded-full border-2"
                      style={{ borderColor: theme.color.light500 }}
                    />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setTargetSegment("targeting")}
                  className="flex-1 flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all"
                  style={{
                    borderColor:
                      targetSegment === "targeting"
                        ? theme.brandColor.red
                        : theme.color.light500,
                    backgroundColor:
                      targetSegment === "targeting"
                        ? "transparent"
                        : "transparent",
                  }}
                >
                  <span
                    className="text-sm font-medium"
                    style={{
                      color:
                        targetSegment === "targeting"
                          ? theme.brandColor.red
                          : theme.color.gray500,
                    }}
                  >
                    Targeting
                  </span>
                  {targetSegment === "targeting" ? (
                    React.createElement(IoCheckmarkCircle as any, {
                      className: "w-5 h-5",
                      style: { color: theme.brandColor.red },
                    })
                  ) : (
                    <div
                      className="w-5 h-5 rounded-full border-2"
                      style={{ borderColor: theme.color.light500 }}
                    />
                  )}
                </button>
              </div>

              {/* Select Target - Show when Targeting is selected */}
              {targetSegment === "targeting" && (
                <div
                  className="mt-6 bg-white rounded-lg p-4 border"
                  style={{ borderColor: theme.color.light300 }}
                >
                  <h3
                    className="text-base font-semibold mb-4"
                    style={{ color: theme.color.gray500 }}
                  >
                    Select Target
                  </h3>
                  <div className="space-y-2">
                    {isLoadingSegments && (
                      <p
                        className="text-sm"
                        style={{ color: theme.color.gray300 }}
                      >
                        Loading segments...
                      </p>
                    )}
                    {segmentsError && !isLoadingSegments && (
                      <p
                        className="text-sm"
                        style={{ color: theme.brandColor.red }}
                      >
                        {segmentsError}
                      </p>
                    )}
                    {!isLoadingSegments &&
                      !segmentsError &&
                      segments.length === 0 && (
                        <p
                          className="text-sm"
                          style={{ color: theme.color.gray300 }}
                        >
                          No segments available.
                        </p>
                      )}
                    {!isLoadingSegments &&
                      !segmentsError &&
                      segments.length > 0 && (
                        <>
                          {segments.map((segment) => {
                            const segmentId = String(segment.id);
                            const isSelected = selectedSegment === segmentId;
                            const amount =
                              segment.amountOfPc ?? segment.amountOfPC ?? 0;

                            return (
                              <button
                                key={segmentId}
                                type="button"
                                onClick={() => setSelectedSegment(segmentId)}
                                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 transition-all"
                                style={{
                                  borderColor: isSelected
                                    ? theme.brandColor.red
                                    : theme.color.light300,
                                  backgroundColor: isSelected
                                    ? theme.brandColor.red + "10"
                                    : "transparent",
                                }}
                              >
                                <div className="flex items-center gap-3">
                                  {isSelected ? (
                                    <div
                                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{
                                        backgroundColor: theme.brandColor.red,
                                      }}
                                    >
                                      <div
                                        className="w-2 h-2 rounded-full"
                                        style={{
                                          backgroundColor: theme.color.light100,
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <div
                                      className="w-5 h-5 rounded-full border-2 flex-shrink-0"
                                      style={{
                                        borderColor: theme.color.light500,
                                      }}
                                    />
                                  )}
                                  <span
                                    className="text-sm font-medium"
                                    style={{ color: theme.color.gray500 }}
                                  >
                                    {segment.segmentName}
                                  </span>
                                </div>
                                <span
                                  className="text-sm"
                                  style={{ color: theme.color.gray200 }}
                                >
                                  {amount} คน
                                </span>
                              </button>
                            );
                          })}
                        </>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Submit Buttons */}
      <div className="fixed bottom-0 left-64 right-0 bg-gray-50 p-4 z-10">
        <div className="max-w-7xl mx-auto flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate("/rich-menu")}
            className="px-6 py-2 rounded-lg border-2 text-sm font-medium transition-all"
            style={{
              borderColor: theme.color.light500,
              backgroundColor: theme.color.light100,
              color: theme.color.gray500,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "0.9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-6 py-2 rounded-lg border-0 text-sm font-medium transition-all text-white flex items-center gap-2"
            style={{
              backgroundColor:
                isFormValid && !isSubmitting
                  ? theme.brandColor.red
                  : theme.color.light300,
              color: theme.color.light100,
              cursor: isFormValid && !isSubmitting ? "pointer" : "not-allowed",
            }}
            disabled={!isFormValid || isSubmitting}
            onMouseEnter={(e) => {
              if (!isFormValid || isSubmitting) return;
              e.currentTarget.style.opacity = "0.9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </div>

      {/* Template Dialog */}
      <TemplateDialog
        isOpen={isTemplateDialogOpen}
        currentTemplateId={selectedTemplate?.id}
        onClose={() => setIsTemplateDialogOpen(false)}
        onApply={(template) => {
          console.log("Selected template:", template);
          setSelectedTemplate(template);
          // Create actions based on selected template
          const newActions = createActionsFromTemplate(template);
          setActions(newActions);
          setIsTemplateDialogOpen(false);
        }}
        uploadedImageSize={uploadedImageSize}
      />
    </Layout>
  );
};

export default ModifyRichMenuPage;
